import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";

const authorizationForClaim = (claim) => {
    if (claim?.amt == 0) {
        return {
            authorization: "y",
            authorization_code: "Capitation",
        };
    }

    if (claim?.authorization_code) {
        return {
            authorization: "y",
            authorization_code: claim.authorization_code,
        };
    }

    return {
        authorization: "n",
        authorization_code: null,
    };
};

// update cluster
const updateClusterForEncounterDiagnosis = async (cluster, encounter) => {

    if (cluster && encounter) {
        let newCluster = cluster;
        // update encounter
        newCluster?.encounters?.forEach((x, y) => {

            if (x.id === encounter.id) {

                // update here
                newCluster.encounters[y].diagnosis = encounter?.diagnosis;

            }

        });

        return newCluster;
    } else {
        return null;
    }

}

// add
const addClaimSave = async (encounterId, newVal) => {
    const authorization = authorizationForClaim(newVal);
    const approved = authorization.authorization === "y"
        ? { approved_qty: newVal.qty, approved_price: newVal.amt }
        : {};

    const { res } = await postItem("claim", {
        encounter_id: encounterId,
        item: newVal.claim,
        sent_qty: newVal.qty,
        sent_price: newVal.amt,
        standard_price: newVal.standard,
        authorization: authorization.authorization,
        authorization_code: authorization.authorization_code,
        ...approved,
        status: newVal.status,
        refresh_activity_rank: true,
    });

    if (res.value) {

        res.value.data.claim = res.value.data.item;
        res.value.data.qty = res.value.data.sent_qty;
        res.value.data.amt = res.value.data.sent_price;
        res.value.data.standard = res.value.data.standard_price;

        return res.value.data;

    } else {
        return null;
    }

};

// update
const updateSavedClaim = async (claimId, newVal) => {

    const { res } = await putItem("claim", claimId, {
        ...authorizationForClaim(newVal),
        item: newVal.claim,
        sent_qty: newVal.qty,
        sent_price: newVal.amt,
        refresh_activity_rank: true,
    });

    if (res.value) {

        res.value.data.claim = res.value.data.item;
        res.value.data.qty = res.value.data.sent_qty;
        res.value.data.amt = res.value.data.sent_price;

        return res.value.data;

    } else {
        return null;
    }

};

// delete
const deleteSavedClaim = async (claimId, newVal) => {

    const { res } = await putItem("claim", claimId, {
        ...newVal,
        refresh_activity_rank: true,
    });

    if (res.value) {
        return res.value.data;
    } else {
        return null;
    }

};

// update
const updateSavedEnrollee = async (encounterId, newVal) => {

    const { res } = await putItem("encounter", encounterId, newVal);

    if (res.value) {
        return res.value.data;
    } else {
        return null;
    }

};

const normalizeDiagnosis = (value) => String(value || "").trim().replace(/\s+/g, " ");

const diagnosisChanged = (currentValue, newValue) => {
    return normalizeDiagnosis(currentValue).toLocaleLowerCase() !== normalizeDiagnosis(newValue).toLocaleLowerCase();
};

// save diagnosis
const saveDiagnosis = async (encounter, diagnosis) => {
    const newDiagnosis = normalizeDiagnosis(diagnosis);

    if (!encounter?.id || !newDiagnosis) {
        return null;
    }

    if (!diagnosisChanged(encounter.diagnosis, newDiagnosis)) {
        return encounter;
    }

    const { res } = await putItem("encounter/diagnosis", encounter.id, {
        diagnosis: newDiagnosis,
        refresh_activity_rank: true,
    });

    if (res.value) {
        return res.value.data;
    } else {
        return null;
    }

};

// delete
const deleteSavedEnrollee = async (encounterId, newVal) => {

    const { res } = await putItem("encounter", encounterId, newVal);

    if (res.value) {
        return res.value.data;
    } else {
        return null;
    }

};

// sync
const syncSavedEnrollee = async (val, cluster) => {

    // defualt
    let newVal = {};

    // master confirmation
    if (val.encounter?.encounter_date_set) {

        // work on deletion
        if (!val.encounter.claims.length || val.encounter.status === "d") {

            newVal = await deleteSavedEnrollee(val.encounter.id, {
                status: "d",
                stores: val.encounter?.encounter_cluster_id,
                encounter_cluster_id: 0,
            });

            return newVal;

        } else {

            let hasChanged = false;
            let diagnosisHasChanged = false;

            cluster.encounters.forEach((x) => {
                if (x.editId === val.encounter.editId) {
                    diagnosisHasChanged = val.encounter?.old_diagnosis !== undefined &&
                        diagnosisChanged(val.encounter.old_diagnosis, val.encounter.diagnosis);

                    if (diagnosisHasChanged ||
                        val.encounter?.old_encounter_date ||
                        val.encounter?.old_encounter_date_end) {
                        // set to true
                        hasChanged = true;
                    }
                }
            });

            if (hasChanged) {

                if (diagnosisHasChanged) {
                    const previousEncounter = {
                        ...val.encounter,
                        diagnosis: val.encounter.old_diagnosis,
                    };
                    const savedEncounter = await saveDiagnosis(previousEncounter, val.encounter.diagnosis);

                    if (!savedEncounter) {
                        return null;
                    }

                    val.encounter.diagnosis = savedEncounter.diagnosis;
                    val.encounter.stores = savedEncounter.stores;
                }

                const dateUpdate = {};

                if (val.encounter?.old_encounter_date) {
                    dateUpdate.encounter_date = val.encounter.encounter_date;
                }

                if (val.encounter?.old_encounter_date_end) {
                    dateUpdate.encounter_date_end = val.encounter.encounter_date_end;
                }

                if (!Object.keys(dateUpdate).length) {
                    return val.encounter;
                }

                newVal = await updateSavedEnrollee(val.encounter.id, dateUpdate); // newVal is null if it fails

                return newVal;

            }

        }

    }

};

const removeEmptyBillingEncounter = async (clusterId, encounterId) => {
    const { res, err } = await postItem(
        `provider/billing/clusters/${clusterId}/encounters/${encounterId}/remove`,
        {},
    );

    return res.value
        ? { data: res.value.data, error: null }
        : { data: null, error: err.value || "The empty encounter could not be removed." };
};

// new encounter
// add
const addEncounterWithClaims = async (encounter) => {

    // clean up encounter
    // let keysToRemove = ["scheme", "claims", "created_at", "updated_at"];
    let keysToRemove = ["id", "scheme", "encounter", "plan", "created_at", "updated_at", "editId", "claims", "status"];
    let newEncounter = {
        ...Object.fromEntries(
            Object.entries(encounter).filter(
                ([key]) => !keysToRemove.includes(key)
            )
        ),
    };

    // Billing-cluster encounters stay open until their guarded q -> p submit.
    // Preserve the legacy direct-create status for callers without a cluster.
    newEncounter.status = encounter.encounter_cluster_id ? "q" : "p";

    // setup claims
    let newClaims = [];

    // clean up claims
    encounter.claims.filter(x => x?.stores?.system_generated !== "professional_fee" && !x?._professional_fee_preview).forEach((x) => {
        if (x.status === "a") {
            const authorization = authorizationForClaim(x);
            const approved = authorization.authorization === "y"
                ? { approved_qty: x.qty, approved_price: x.amt }
                : {};

            // load
            newClaims.push({
                encounter_id: null,
                item: x.claim,
                sent_qty: x.qty,
                sent_price: x.amt,
                standard_price: x.standard,
                authorization: authorization.authorization,
                authorization_code: authorization.authorization_code,
                ...approved,
                status: x.status,
            });
        }
    });

    // put all together
    let newVal = { encounter: newEncounter, claims: newClaims };

    // post item
    const endpoint = encounter.encounter_cluster_id
        ? `provider/billing/clusters/${encounter.encounter_cluster_id}/encounters`
        : "claim/enrollee/create/bulk";
    const { res, err } = await postItem(endpoint, newVal);

    if (res.value) {
        // return
        return { data: res.value.data };

    } else {

        // failure
        return { error: err.value || "Provider billing encounter could not be saved." };

    }

};


export {
    addClaimSave,
    updateSavedClaim,
    updateClusterForEncounterDiagnosis,
    saveDiagnosis,
    deleteSavedClaim,
    syncSavedEnrollee,
    removeEmptyBillingEncounter,
    addEncounterWithClaims,
}
